import fs from 'node:fs'
import path from 'node:path'
import * as p from '@clack/prompts'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import type { FormSchema, GeneratedFile, Schema } from '@core-engine/schema/types.js'
import {
  buildRenamePlanAliasMap,
  type RenamePlan,
  transformContentForRenamePlan
} from '@core-engine/snapshots/ast-substitution.js'
import {
  deleteProjectFile,
  hasConflictMarkers,
  mergeGeneratedFile,
  readProjectFile,
  writeProjectFile
} from '@core-engine/snapshots/merge.js'
import {
  createManifest,
  getCurrentCliVersion,
  loadManifest,
  saveManifest,
  snapshotTargetPath,
  writeSnapshotFiles
} from '@core-engine/snapshots/store.js'
import { sortGeneratedFiles } from '@core-engine/utils/generated-files.js'
import { formatContentForSnapshotMerge } from '@next-adapter/snapshots/format-for-merge.js'
import { reconstructSnapshotFiles } from '@next-adapter/snapshots/reconstruct.js'

function normalize(content: string): string {
  return content.replace(/\r\n/g, '\n')
}

function buildMergeInput(
  cwd: string,
  baseFile: GeneratedFile,
  localContent: string,
  remoteFile: GeneratedFile
): { baseFile: GeneratedFile; localContent: string; remoteFile: GeneratedFile } {
  const formattedBase = formatContentForSnapshotMerge(cwd, remoteFile.path, baseFile.content)
  if (formattedBase == null) {
    return { baseFile, localContent, remoteFile }
  }

  const formattedLocal = formatContentForSnapshotMerge(cwd, remoteFile.path, localContent)
  const formattedRemote = formatContentForSnapshotMerge(cwd, remoteFile.path, remoteFile.content)
  if (formattedLocal == null || formattedRemote == null) {
    return { baseFile, localContent, remoteFile }
  }

  return {
    baseFile: {
      path: remoteFile.path,
      content: formattedBase
    },
    localContent: formattedLocal,
    remoteFile: {
      path: remoteFile.path,
      content: formattedRemote
    }
  }
}

function isInteractiveSession(interactive: boolean): boolean {
  return interactive && Boolean(process.stdin.isTTY) && process.env.CI !== 'true'
}

async function promptNoBaseDecision(filePath: string): Promise<'overwrite' | 'skip' | 'backup'> {
  const answer = await p.select({
    message: `File already exists without snapshot base: ${filePath}`,
    options: [
      { value: 'backup', label: 'Backup and overwrite', hint: 'recommended' },
      { value: 'overwrite', label: 'Overwrite' },
      { value: 'skip', label: 'Skip' }
    ],
    initialValue: 'backup'
  })

  if (p.isCancel(answer)) {
    throw new Error('Generation cancelled.')
  }

  return answer as 'overwrite' | 'skip' | 'backup'
}

function writeBackupFile(cwd: string, filePath: string, content: string, suffix = '.bak'): void {
  const backupPath = `${snapshotTargetPath(cwd, filePath)}${suffix}`
  fs.mkdirSync(path.dirname(backupPath), { recursive: true })
  fs.writeFileSync(backupPath, content, 'utf-8')
}

export interface ApplyGeneratedFilesOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  scope: string
  schemaJson: Schema | FormSchema | { name: string }
  generatedFiles: GeneratedFile[]
  force: boolean
  acceptGenerated?: boolean
  interactive: boolean
  renamePlan?: RenamePlan
}

export interface ApplyGeneratedFilesSummary {
  writtenFresh: string[]
  overwritten: string[]
  mergedCleanly: string[]
  acceptedGenerated: string[]
  conflictPaths: string[]
  orphanedKept: string[]
  orphanedDeleted: string[]
  skipped: string[]
  skippedCleared: string[]
  tombstoneCleared?: string
}

export async function applyGeneratedFiles({
  cwd,
  config,
  scope,
  schemaJson,
  generatedFiles,
  force,
  acceptGenerated = false,
  interactive,
  renamePlan
}: ApplyGeneratedFilesOptions): Promise<ApplyGeneratedFilesSummary> {
  const manifest = loadManifest(cwd, scope)
  const summary: ApplyGeneratedFilesSummary = {
    writtenFresh: [],
    overwritten: [],
    mergedCleanly: [],
    acceptedGenerated: [],
    conflictPaths: [],
    orphanedKept: [],
    orphanedDeleted: [],
    skipped: [],
    skippedCleared: []
  }

  const remoteFiles = sortGeneratedFiles(generatedFiles)
  const remotePaths = new Set(remoteFiles.map((file) => file.path))
  const skipped = new Set(force ? [] : (manifest?.skipped ?? []))
  const interactiveSession = isInteractiveSession(interactive)
  const aliasMap = renamePlan ? buildRenamePlanAliasMap(renamePlan) : new Map<string, string>()
  const consumedBasePaths = new Set<string>()

  let baseFiles = new Map<string, GeneratedFile>()
  let mismatchedPaths = new Set<string>()
  let trackedBasePaths = new Set<string>()
  if (manifest) {
    const reconstructed = reconstructSnapshotFiles(cwd, config, manifest)
    baseFiles = new Map(reconstructed.files.map((file) => [file.path, file]))
    mismatchedPaths = new Set(reconstructed.mismatchedPaths)
    trackedBasePaths = new Set(reconstructed.trackedPaths)
  }

  for (const file of remoteFiles) {
    if (skipped.has(file.path) && !force) {
      continue
    }

    if (force) {
      const localContent = readProjectFile(cwd, file.path)
      writeProjectFile(cwd, file)
      if (localContent == null) {
        summary.writtenFresh.push(file.path)
      } else if (normalize(localContent) !== normalize(file.content)) {
        summary.overwritten.push(file.path)
      }
      continue
    }

    const aliasPath = aliasMap.get(file.path)
    let baseFile = baseFiles.get(file.path)
    let localContent = readProjectFile(cwd, file.path)
    let aliasedLocalPath: string | null = null

    if (!baseFile && aliasPath && !mismatchedPaths.has(aliasPath)) {
      const aliasedBase = baseFiles.get(aliasPath)
      if (aliasedBase) {
        const transformedBase = renamePlan
          ? transformContentForRenamePlan(aliasedBase.content, aliasPath, renamePlan, file.path)
          : null

        if (!transformedBase?.parseFailed) {
          baseFile = {
            path: file.path,
            content: transformedBase ? transformedBase.content : aliasedBase.content
          }
          consumedBasePaths.add(aliasPath)
        }
      }
    }

    if (baseFile && localContent == null && aliasPath) {
      const aliasedLocal = readProjectFile(cwd, aliasPath)
      if (aliasedLocal != null) {
        const transformedLocal = renamePlan
          ? transformContentForRenamePlan(aliasedLocal, aliasPath, renamePlan, file.path)
          : null

        if (!transformedLocal?.parseFailed) {
          localContent = transformedLocal ? transformedLocal.content : aliasedLocal
          aliasedLocalPath = aliasPath
        }
      }
    }

    if (baseFile && renamePlan && baseFile.path === file.path) {
      const transformedBase = transformContentForRenamePlan(baseFile.content, file.path, renamePlan)
      if (!transformedBase.parseFailed) {
        baseFile = {
          path: file.path,
          content: transformedBase.content
        }
      }
    }

    if (localContent != null && renamePlan && !aliasedLocalPath) {
      const transformedLocal = transformContentForRenamePlan(localContent, file.path, renamePlan)
      if (!transformedLocal.parseFailed) {
        localContent = transformedLocal.content
      }
    }

    if (localContent == null) {
      writeProjectFile(cwd, file)
      summary.writtenFresh.push(file.path)
      continue
    }

    if (!baseFile || mismatchedPaths.has(file.path)) {
      const decision = force
        ? 'overwrite'
        : interactiveSession
          ? await promptNoBaseDecision(file.path)
          : 'backup'

      if (decision === 'skip') {
        skipped.add(file.path)
        summary.skipped.push(file.path)
        continue
      }

      if (decision === 'backup') {
        writeBackupFile(cwd, file.path, localContent)
      }

      writeProjectFile(cwd, file)
      if (aliasedLocalPath) {
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      summary.writtenFresh.push(file.path)
      continue
    }

    if (normalize(localContent) === normalize(file.content)) {
      if (aliasedLocalPath) {
        writeProjectFile(cwd, file)
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      continue
    }

    if (acceptGenerated && hasConflictMarkers(localContent)) {
      writeProjectFile(cwd, file)
      if (aliasedLocalPath) {
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      summary.acceptedGenerated.push(file.path)
      continue
    }

    const mergeInput = buildMergeInput(cwd, baseFile, localContent, file)

    if (normalize(mergeInput.localContent) === normalize(mergeInput.remoteFile.content)) {
      if (aliasedLocalPath) {
        writeProjectFile(cwd, mergeInput.remoteFile)
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      continue
    }

    if (normalize(mergeInput.localContent) === normalize(mergeInput.baseFile.content)) {
      writeProjectFile(cwd, mergeInput.remoteFile)
      if (aliasedLocalPath) {
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      summary.mergedCleanly.push(file.path)
      continue
    }

    if (normalize(mergeInput.remoteFile.content) === normalize(mergeInput.baseFile.content)) {
      if (aliasedLocalPath) {
        writeProjectFile(cwd, {
          path: file.path,
          content: mergeInput.localContent
        })
        deleteProjectFile(cwd, aliasedLocalPath)
      }
      continue
    }

    const merged = mergeGeneratedFile(
      mergeInput.baseFile,
      mergeInput.localContent,
      mergeInput.remoteFile
    )
    writeProjectFile(cwd, merged.conflict && acceptGenerated ? mergeInput.remoteFile : merged.file)
    if (aliasedLocalPath) {
      deleteProjectFile(cwd, aliasedLocalPath)
    }
    if (merged.conflict) {
      if (acceptGenerated) {
        summary.acceptedGenerated.push(file.path)
      } else {
        summary.conflictPaths.push(file.path)
      }
    } else {
      summary.mergedCleanly.push(file.path)
    }
  }

  const orphanCandidatePaths = Array.from(
    new Set([...trackedBasePaths, ...baseFiles.keys()])
  ).sort()

  for (const filePath of orphanCandidatePaths) {
    if (consumedBasePaths.has(filePath)) {
      continue
    }

    if (remotePaths.has(filePath)) {
      continue
    }

    const localContent = readProjectFile(cwd, filePath)
    if (localContent == null) {
      continue
    }

    if (force) {
      deleteProjectFile(cwd, filePath)
      summary.orphanedDeleted.push(filePath)
      continue
    }

    const baseFile = baseFiles.get(filePath)
    if (!baseFile) {
      continue
    }

    if (normalize(localContent) === normalize(baseFile.content)) {
      deleteProjectFile(cwd, filePath)
      summary.orphanedDeleted.push(filePath)
    } else {
      summary.orphanedKept.push(filePath)
    }
  }

  const nextSkipped = Array.from(skipped)
    .filter((filePath) => remotePaths.has(filePath))
    .sort()
  summary.skippedCleared = Array.from(skipped)
    .filter((filePath) => !remotePaths.has(filePath))
    .sort()

  const trackedFiles = remoteFiles.filter((file) => !nextSkipped.includes(file.path))
  writeSnapshotFiles(cwd, scope, trackedFiles)
  const nextManifest = createManifest(
    scope,
    schemaJson,
    getCurrentCliVersion(),
    trackedFiles,
    nextSkipped
  )
  saveManifest(cwd, scope, nextManifest)

  return summary
}
